import {
  AddCustomer,
  PostCustomer2FA,
  PostEraseCustomer,
  GetCustomers,
  PutCustomer,
  GetCustomerSubscription,
  GetCustomerSubscriptionAppointments,
  GetCustomerSubscriptions,
  GetCustomerSubscriptionsForCustomer,
  GetCustomerSubscriptionOverviewStats,
  PostCustomerSubscription,
  PutCustomerSubscription,
  DeleteCustomerSubscription,
  PostMergeCustomers,
  GetCustomerMergeSuggestions,
  PostDismissCustomerMergeSuggestion,
  PostBulkSmsEstimate,
  GetCustomerSubscriptionInvoice,
  GetCustomerSubscriptionInvoiceById,
  GetCustomerSubscriptionEarlyExitEstimate,
  GetCustomerSubscriptionValidityPreview,
  PutCustomerSubscriptionCommitmentEndOfTerm,
  PostCustomerSubscriptionCommitment,
  PostCustomerSubscriptionExitChargeDocument,
  GetCustomerSubscriptionEvents,
  GetCustomerSubscriptionNotifications,
  PutCustomerSubscriptionInvoice,
  DeleteCustomerSubscriptionInvoice,
  PostCustomerSubscriptionInvoice,
  PostCustomerSubscriptionEstimates,
  PutSubscriptionInvoiceShare,
  GetSubscriptionInvoiceShares,
  PostUnlinkSubscriptionPeriodEstimate,
  PostLinkSubscriptionPeriodDocument,
  PostSendSubscriptionPeriodDocument,
  PostCustomerSubscriptionCredit,
  DeleteCustomerSubscriptionCredit,
  PostCustomerSubscriptionLinkAppointments,
  GetCustomer,
  GetCustomerContactCustomers,
  PostCustomerSubscriptionFreeze,
  DeleteScheduledSubscriptionFreeze,
  PutScheduledSubscriptionFreeze,
  GetScheduledSubscriptionFreeze,
  DeleteCustomerPrescheduled,
  GetCustomerAppointments,
  GetCheckIfCustomerExists,
  GetUserFilterCustomer,
  PostUserFilterCustomer,
  PutUserFilterCustomer,
  DeleteUserFilterCustomer,
  GetUserFilterCompany,
  PostUserFilterCompany,
  PutUserFilterCompany,
  DeleteUserFilterCompany,
  PostExportCustomers,
  GetCustomerNote,
  PostCustomerNote,
  PutCustomerNote,
  DeleteCustomerNote,
  GetCustomerNotes,
  GetCustomerNoteTag,
  PostCustomerMarketingConsentBulk,
  PostCustomerMarketingOptOut,
  DeleteCustomerMarketingOptOut,
} from "@/server-types";
import { post, get, put, del } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { queryClient } from "..";

export const useGetCustomer = ({
  customerId,
  enabled,
}: {
  customerId?: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["customer", customerId],
    queryFn: () => get<GetCustomer>(`/customer/${customerId!}`),
    enabled: enabled && customerId != null && customerId !== -1,
  });
};

export const useGetCustomerContactCustomers = ({
  customerId,
  enabled,
}: {
  customerId?: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["customer-contact-customers", customerId],
    queryFn: () =>
      get<GetCustomerContactCustomers>(
        `/customer/${customerId!}/contact-customers`,
      ),
    enabled: enabled && customerId != null && customerId !== -1,
  });
};

export const usePostMergeCustomers = () => {
  return useCustomMutation({
    mutationFn: (body: PostMergeCustomers["body"]) =>
      post<PostMergeCustomers>("/customer/merge", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });
    },
  });
};

export const useBulkSmsEstimate = (
  body: PostBulkSmsEstimate["body"],
  options?: { enabled?: boolean },
) => {
  return useCustomQuery({
    queryKey: ["bulk-sms-estimate", body],
    queryFn: () =>
      post<PostBulkSmsEstimate>("/customer/bulk_sms/estimate", body),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
};

export const usePostDismissCustomerMergeSuggestion = () => {
  return useCustomMutation({
    mutationFn: (body: PostDismissCustomerMergeSuggestion["body"]) =>
      post<PostDismissCustomerMergeSuggestion>(
        "/customer/merge-suggestions/dismiss",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getCustomerList", "mergeSuggestions"],
      });
    },
  });
};

export const useGetCustomerMergeSuggestions = (enabled = true) => {
  return useCustomQuery({
    // Nested under the list so every refresh of the customer list refreshes the suggestions too.
    queryKey: ["getCustomerList", "mergeSuggestions"],
    queryFn: () =>
      get<GetCustomerMergeSuggestions>("/customer/merge-suggestions"),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * POST /customers
 * /
 */

export const useAddCustomer = () => {
  return useCustomMutation({
    mutationFn: (body: AddCustomer["body"]) =>
      post<AddCustomer>("/customer", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });
    },
  });
};

export const useConfirmCustomer2FA = () => {
  return useCustomMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: number;
      body: PostCustomer2FA["body"];
    }) => post<PostCustomer2FA>(`/customer/${customerId}/2fa`, body),
  });
};

export const useEraseCustomer = () => {
  return useCustomMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: number;
      body: PostEraseCustomer["body"];
    }) => post<PostEraseCustomer>(`/customer/${customerId}/erase`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });
    },
  });
};

export const useGetCustomers = (
  query: GetCustomers["query"] & {
    enabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["customers", query],
    queryFn: () => get<GetCustomers>("/customer", query),
    enabled: query.enabled ?? true,
  });
};

export const useInfiniteGetCustomers = (
  query: Omit<GetCustomers["query"], "locationId"> & {
    locationId?: number;
    businessPremiseId?: string;
  },
) => {
  return useInfiniteQuery({
    queryKey: ["getCustomersInfinite", query],
    queryFn: async ({ pageParam = 0 }) => {
      return get<GetCustomers>("/customer", {
        ...query,
        pagination: {
          ...query.pagination,
          offset: pageParam as number,
        },
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
  });
};

export const useGetCustomersMutation = () => {
  return useCustomMutation({
    mutationFn: (body: GetCustomers["query"]) =>
      get<GetCustomers>("/customer", body),
  });
};

export const usePutCustomer = () => {
  return useCustomMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: number;
      body: PutCustomer["body"];
    }) => put<PutCustomer>(`/customer/${customerId}`, body),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
    },
  });
};

export const usePostCustomerMarketingOptOut = () => {
  return useCustomMutation({
    mutationFn: ({
      customerId,
      body,
    }: {
      customerId: number;
      body: PostCustomerMarketingOptOut["body"];
    }) =>
      post<PostCustomerMarketingOptOut>(
        `/customer/${customerId}/marketing-opt-out`,
        body,
      ),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
      queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });
    },
  });
};

export const usePostCustomerMarketingConsentBulk = () => {
  return useCustomMutation({
    mutationFn: (body: PostCustomerMarketingConsentBulk["body"]) =>
      post<PostCustomerMarketingConsentBulk>(
        "/customer/marketing-consent/bulk",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });
    },
  });
};

export const useDeleteCustomerMarketingOptOut = () => {
  return useCustomMutation({
    mutationFn: ({ customerId }: { customerId: number }) =>
      del<DeleteCustomerMarketingOptOut>(
        `/customer/${customerId}/marketing-opt-out`,
      ),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
      queryClient.invalidateQueries({ queryKey: ["getCustomerList"] });
    },
  });
};

export const useGetCustomerSubscriptions = (
  body?: GetCustomerSubscriptions["body"],
) => {
  return useCustomQuery({
    queryKey: ["customer-subscription", body],
    queryFn: () =>
      post<GetCustomerSubscriptions>("/customer-subscription/search", body),
  });
};

export const useGetCustomerSubscriptionOverviewStats = () => {
  return useCustomQuery({
    queryKey: ["customer-subscription", "overview-stats"],
    queryFn: () =>
      get<GetCustomerSubscriptionOverviewStats>(
        "/customer-subscription/overview-stats",
      ),
  });
};

export const useGetCustomerSubscriptionsForCustomer = (
  {
    customerId,
    status,
    subscriptionIds,
    sortBy,
  }: {
    customerId: number;
    status?: GetCustomerSubscriptionsForCustomer["query"]["status"];
    subscriptionIds?: GetCustomerSubscriptionsForCustomer["query"]["subscriptionIds"];
    sortBy?: GetCustomerSubscriptionsForCustomer["query"]["sortBy"];
  },
  options?: { enabled?: boolean },
) => {
  return useCustomQuery({
    enabled: (options?.enabled ?? true) && customerId !== -1,
    queryKey: [
      "customer-subscription",
      "for-customer",
      customerId,
      { status, subscriptionIds, sortBy },
    ],
    queryFn: () =>
      get<GetCustomerSubscriptionsForCustomer>(
        `/customer/${customerId}/customer-subscriptions`,
        { status, subscriptionIds, sortBy },
      ),
  });
};

export const useGetCustomerSubscription = ({
  customerSubscriptionId,
  enabled,
}: {
  customerSubscriptionId: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    enabled,
    queryKey: ["customer-subscription-by-id", customerSubscriptionId],
    queryFn: () =>
      get<GetCustomerSubscription>(
        `/customer-subscription/${customerSubscriptionId}`,
      ),
  });
};

export const useGetCustomerSubscriptionAppointments = ({
  customerSubscriptionId,
  enabled,
}: {
  customerSubscriptionId: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    enabled,
    queryKey: ["customer-subscription-appointments", customerSubscriptionId],
    queryFn: () =>
      get<GetCustomerSubscriptionAppointments>(
        `/customer-subscription/${customerSubscriptionId}/appointments`,
      ),
  });
};

export const useGetCustomerSubscriptionEvents = ({
  customerSubscriptionId,
  query,
  enabled,
}: {
  customerSubscriptionId: number;
  query: GetCustomerSubscriptionEvents["query"];
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["customer-subscription-events", customerSubscriptionId, query],
    queryFn: () =>
      get<GetCustomerSubscriptionEvents>(
        `/customer-subscription/${customerSubscriptionId}/events`,
        query,
      ),
    enabled: enabled ?? true,
  });
};

export const useGetCustomerSubscriptionNotifications = ({
  customerSubscriptionId,
  query,
  enabled,
}: {
  customerSubscriptionId: number;
  query: GetCustomerSubscriptionNotifications["query"];
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: [
      "customer-subscription-notifications",
      customerSubscriptionId,
      query,
    ],
    queryFn: () =>
      get<GetCustomerSubscriptionNotifications>(
        `/customer-subscription/${customerSubscriptionId}/notifications`,
        query,
      ),
    enabled: enabled ?? true,
  });
};

/** Null while no term is running, so the cancel modal shows the warning only when it applies. */
export const useGetCustomerSubscriptionEarlyExitEstimate = ({
  customerSubscriptionId,
  enabled,
}: {
  customerSubscriptionId: number;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["customer-subscription-early-exit", customerSubscriptionId],
    queryFn: () =>
      get<GetCustomerSubscriptionEarlyExitEstimate>(
        `/customer-subscription/${customerSubscriptionId}/early-exit-estimate`,
      ),
    enabled: enabled ?? true,
    // The default three retries leave the modal on a skeleton for about five seconds
    // before it can say anything; the operator is waiting on this to cancel.
    retry: 1,
  });
};

/** Where the proposed validity dates would leave each running term, before anything is saved. */
export const useGetCustomerSubscriptionValidityPreview = ({
  customerSubscriptionId,
  validFrom,
  validTo,
  enabled,
}: {
  customerSubscriptionId: number;
  validFrom: string;
  validTo: string;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: [
      "customer-subscription-validity-preview",
      customerSubscriptionId,
      validFrom,
      validTo,
    ],
    queryFn: () =>
      get<GetCustomerSubscriptionValidityPreview>(
        `/customer-subscription/${customerSubscriptionId}/validity-preview`,
        { validFrom, validTo },
      ),
    enabled: enabled ?? true,
    // The operator is held at the confirmation dialog until this answers.
    retry: 1,
  });
};

/** Edits what the running term does when it ends, for as long as nothing has acted on it. */
export const useUpdateCustomerSubscriptionCommitmentEndOfTerm = (options?: {
  onSuccess?: () => void;
}) => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PutCustomerSubscriptionCommitmentEndOfTerm["body"];
    }) =>
      put<PutCustomerSubscriptionCommitmentEndOfTerm>(
        `/customer-subscription/${customerSubscriptionId}/commitment-end-of-term`,
        body,
      ),
    onSuccess: async (_, { customerSubscriptionId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
      // Awaited so the old controls stay disabled until the new decision renders.
      await queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });
      options?.onSuccess?.();
    },
  });
};

/** A term changed, so its card, timeline and repriced periods are stale; only the id-scoped read the open view renders is awaited. */
function invalidateCommitmentViews(
  customerSubscriptionId: number,
): Promise<void> {
  queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
  queryClient.invalidateQueries({ queryKey: ["customer-subscription-events"] });
  queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
  return queryClient.invalidateQueries({
    queryKey: ["customer-subscription-by-id", customerSubscriptionId],
  });
}

/** Raises the recorded early-exit charge as an estimate; an invoice is sold at the till. */
export const useIssueCommitmentExitChargeDocument = (options?: {
  onSuccess?: (
    result: PostCustomerSubscriptionExitChargeDocument["response"],
  ) => void;
}) => {
  return useCustomMutation({
    displayNotification: true,
    mutationFn: ({
      customerSubscriptionId,
      commitmentId,
      body,
    }: {
      customerSubscriptionId: number;
      commitmentId: number;
      body: PostCustomerSubscriptionExitChargeDocument["body"];
    }) =>
      post<PostCustomerSubscriptionExitChargeDocument>(
        `/customer-subscription/${customerSubscriptionId}/commitment/${commitmentId}/exit-charge-document`,
        body,
      ),
    onSuccess: async (result, { customerSubscriptionId }) => {
      await invalidateCommitmentViews(customerSubscriptionId);
      options?.onSuccess?.(result);
    },
  });
};

/** Signs a term onto a package that is already running; the server picks the period it starts at. */
export const useSignCustomerSubscriptionCommitment = (options?: {
  onSuccess?: () => void;
}) => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PostCustomerSubscriptionCommitment["body"];
    }) =>
      post<PostCustomerSubscriptionCommitment>(
        `/customer-subscription/${customerSubscriptionId}/commitment`,
        body,
      ),
    onSuccess: async (_, { customerSubscriptionId }) => {
      await invalidateCommitmentViews(customerSubscriptionId);
      options?.onSuccess?.();
    },
  });
};

export const useCreateCustomerSubscription = () => {
  return useCustomMutation({
    mutationFn: ({ body }: { body: PostCustomerSubscription["body"] }) =>
      post<PostCustomerSubscription>("/customer-subscription/", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
    },
  });
};

export const useUpdateCustomerSubscription = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PutCustomerSubscription["body"];
    }) =>
      put<PutCustomerSubscription>(
        `/customer-subscription/${customerSubscriptionId}`,
        body,
      ),
    onSuccess: (_, { customerSubscriptionId, body }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });

      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

export const useDeleteCustomerSubscription = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
    }: {
      customerSubscriptionId: number;
    }) =>
      del<DeleteCustomerSubscription>(
        `/customer-subscription/${customerSubscriptionId}`,
      ),
    onSuccess: (_, { customerSubscriptionId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.removeQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });
      queryClient.removeQueries({
        queryKey: ["customer-subscription-events", customerSubscriptionId],
      });
      queryClient.removeQueries({
        queryKey: ["subscription-invoices", customerSubscriptionId],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

export const useGetSubscriptionInvoiceById = ({
  invoiceId,
  enabled,
}: {
  invoiceId: number | null;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["subscription-invoice", invoiceId],
    queryFn: () =>
      get<GetCustomerSubscriptionInvoiceById>(
        `/customer-subscription/invoices/${invoiceId!}`,
      ),
    enabled: (enabled ?? true) && invoiceId != null,
  });
};

export const useGetSubscriptionInvoices = ({
  customerSubscriptionId,
  query,
  enabled,
}: {
  customerSubscriptionId: number;
  query?: GetCustomerSubscriptionInvoice["query"];
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["subscription-invoices", customerSubscriptionId, query],
    queryFn: () =>
      get<GetCustomerSubscriptionInvoice>(
        `/customer-subscription/${customerSubscriptionId}/invoices`,
        query,
      ),
    enabled: enabled ?? true,
  });
};

export const useCreateSubscriptionInvoice = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PostCustomerSubscriptionInvoice["body"];
    }) =>
      post<PostCustomerSubscriptionInvoice>(
        `/customer-subscription/${customerSubscriptionId}/invoices`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
    },
  });
};

export const useUpdateSubscriptionInvoice = () => {
  return useCustomMutation({
    mutationFn: ({
      invoiceId,
      body,
    }: {
      invoiceId: number;
      body: PutCustomerSubscriptionInvoice["body"];
    }) =>
      put<PutCustomerSubscriptionInvoice>(
        `/customer-subscription/invoices/${invoiceId}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
    },
  });
};

export const useDeleteSubscriptionInvoice = () => {
  return useCustomMutation({
    mutationFn: ({
      invoiceId,
      body,
    }: {
      invoiceId: number;
      body?: DeleteCustomerSubscriptionInvoice["body"];
    }) =>
      del<DeleteCustomerSubscriptionInvoice>(
        `/customer-subscription/invoices/${invoiceId}`,
        body ?? { cancelDocumentIds: [] },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
    },
  });
};

export const useUpdateSubscriptionShare = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationFn: ({
      shareId,
      body,
    }: {
      shareId: number;
      body: PutSubscriptionInvoiceShare["body"];
    }) =>
      put<PutSubscriptionInvoiceShare>(
        `/customer-subscription/shares/${shareId}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
    },
  });
};

export const useGetSubscriptionInvoiceShares = (
  query: GetSubscriptionInvoiceShares["query"],
) => {
  return useCustomQuery({
    // Under "customer-subscription", so every subscription mutation refreshes it.
    queryKey: ["customer-subscription", "shares", query],
    queryFn: () =>
      get<GetSubscriptionInvoiceShares>("/customer-subscription/shares", query),
    enabled: query.spaceInvoicesDocumentIds.length > 0,
  });
};

export const useIssueCustomerSubscriptionEstimates = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationFn: ({
      body,
    }: {
      body: PostCustomerSubscriptionEstimates["body"];
    }) =>
      post<PostCustomerSubscriptionEstimates>(
        "/customer-subscription/estimates",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
      queryClient.invalidateQueries({
        queryKey: ["company-funded-subscriptions"],
      });
    },
  });
};

export const useUnlinkSubscriptionPeriodEstimate = () => {
  return useCustomMutation({
    mutationFn: ({
      invoiceId,
      body,
    }: {
      invoiceId: number;
      body: PostUnlinkSubscriptionPeriodEstimate["body"];
    }) =>
      post<PostUnlinkSubscriptionPeriodEstimate>(
        `/customer-subscription/invoices/${invoiceId}/unlink-estimate`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
    },
  });
};

export const useLinkSubscriptionPeriodDocument = () => {
  return useCustomMutation({
    mutationFn: ({
      invoiceId,
      body,
    }: {
      invoiceId: number;
      body: PostLinkSubscriptionPeriodDocument["body"];
    }) =>
      post<PostLinkSubscriptionPeriodDocument>(
        `/customer-subscription/invoices/${invoiceId}/link-document`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
    },
  });
};

export const useSendSubscriptionPeriodDocument = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationFn: ({ invoiceId }: { invoiceId: number }) =>
      post<PostSendSubscriptionPeriodDocument>(
        `/customer-subscription/invoices/${invoiceId}/send`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
    },
  });
};

export const usePostCustomerSubscriptionCredit = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PostCustomerSubscriptionCredit["body"];
    }) =>
      post<PostCustomerSubscriptionCredit>(
        `/customer-subscription/${customerSubscriptionId}/credits`,
        body,
      ),
    onSuccess: (_, { customerSubscriptionId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });
    },
  });
};

export const useDeleteCustomerSubscriptionCredit = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      creditId,
    }: {
      customerSubscriptionId: number;
      creditId: number;
    }) =>
      del<DeleteCustomerSubscriptionCredit>(
        `/customer-subscription/${customerSubscriptionId}/credits/${creditId}`,
      ),
    onSuccess: (_, { customerSubscriptionId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });
    },
  });
};

export const usePostCustomerSubscriptionLinkAppointments = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PostCustomerSubscriptionLinkAppointments["body"];
    }) =>
      post<PostCustomerSubscriptionLinkAppointments>(
        `/customer-subscription/${customerSubscriptionId}/link-appointments`,
        body,
      ),
    onSuccess: (_, { customerSubscriptionId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "customer-subscription-appointments",
          customerSubscriptionId,
        ],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

export const usePostCustomerSubscriptionFreeze = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      body,
    }: {
      customerSubscriptionId: number;
      body: PostCustomerSubscriptionFreeze["body"];
    }) =>
      post<PostCustomerSubscriptionFreeze>(
        `/customer-subscription/${customerSubscriptionId}/freeze`,
        body,
      ),
    onSuccess: (_, { customerSubscriptionId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["subscription-invoices", customerSubscriptionId],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

export const useDeleteScheduledSubscriptionFreeze = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      scheduledFreezeId,
      query,
    }: {
      customerSubscriptionId: number;
      scheduledFreezeId: number;
      query?: DeleteScheduledSubscriptionFreeze["query"];
    }) =>
      del<DeleteScheduledSubscriptionFreeze>(
        `/customer-subscription/${customerSubscriptionId}/freeze/${scheduledFreezeId}`,
        undefined,
        query,
      ),
    onSuccess: (_, { customerSubscriptionId, scheduledFreezeId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });
      queryClient.removeQueries({
        queryKey: [
          "scheduled-subscription-freeze",
          customerSubscriptionId,
          scheduledFreezeId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["subscription-invoices", customerSubscriptionId],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

export const usePutScheduledSubscriptionFreeze = () => {
  return useCustomMutation({
    mutationFn: ({
      customerSubscriptionId,
      scheduledFreezeId,
      body,
    }: {
      customerSubscriptionId: number;
      scheduledFreezeId: number;
      body: PutScheduledSubscriptionFreeze["body"];
    }) =>
      put<PutScheduledSubscriptionFreeze>(
        `/customer-subscription/${customerSubscriptionId}/freeze/${scheduledFreezeId}`,
        body,
      ),
    onSuccess: (_, { customerSubscriptionId, scheduledFreezeId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id", customerSubscriptionId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "scheduled-subscription-freeze",
          customerSubscriptionId,
          scheduledFreezeId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["subscription-invoices", customerSubscriptionId],
      });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

export const useGetScheduledSubscriptionFreeze = ({
  customerSubscriptionId,
  scheduledFreezeId,
  enabled = true,
}: {
  customerSubscriptionId: number | null;
  scheduledFreezeId: number | null;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: [
      "scheduled-subscription-freeze",
      customerSubscriptionId,
      scheduledFreezeId,
    ],
    queryFn: () =>
      get<GetScheduledSubscriptionFreeze>(
        `/customer-subscription/${customerSubscriptionId!}/freeze/${scheduledFreezeId!}`,
      ),
    enabled:
      enabled && customerSubscriptionId != null && scheduledFreezeId != null,
  });
};

export const useDeleteCustomerPrescheduled = () => {
  return useCustomMutation({
    mutationFn: ({
      body,
      customerId,
    }: {
      body: DeleteCustomerPrescheduled["body"];
      customerId: number;
    }) =>
      del<DeleteCustomerPrescheduled>(
        `/customer/${customerId}/prescheduled`,
        body,
      ),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
      queryClient.invalidateQueries({ queryKey: ["prescheduled"] });
      queryClient.invalidateQueries({ queryKey: ["customer-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
    },
  });
};

export const useGetCustomerAppointments = ({
  customerId,
  query,
  enabled,
}: {
  customerId: number;
  query: GetCustomerAppointments["query"];
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["customer-appointments", customerId, query],
    queryFn: () =>
      get<GetCustomerAppointments>(
        `/customer/${customerId}/appointment`,
        query,
      ),
    enabled: enabled ?? true,
  });
};

export const useGetCheckIfCustomerExists = (
  query: GetCheckIfCustomerExists["query"],
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["useGetCheckIfCustomerExists", query],
    queryFn: () =>
      get<GetCheckIfCustomerExists>("/customer/check-if-exists", query),
    enabled: enabled ?? true,
  });
};

// Customer List Filter hooks

export const useGetCustomerListFilters = ({
  disabled,
}: {
  disabled?: boolean;
} = {}) => {
  return useCustomQuery({
    queryKey: ["customer-list-filters"],
    queryFn: () => get<GetUserFilterCustomer>("/user/filter/customer"),
    enabled: !disabled,
  });
};

export const useCreateCustomerListFilter = () => {
  return useCustomMutation({
    mutationFn: (body: PostUserFilterCustomer["body"]) =>
      post<PostUserFilterCustomer>("/user/filter/customer", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-list-filters"] });
    },
  });
};

export const useUpdateCustomerListFilter = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: PutUserFilterCustomer["body"];
    }) => put<PutUserFilterCustomer>(`/user/filter/customer/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-list-filters"] });
    },
  });
};

export const useDeleteCustomerListFilter = () => {
  return useCustomMutation({
    mutationFn: ({ id }: { id: number }) =>
      del<DeleteUserFilterCustomer>(`/user/filter/customer/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-list-filters"] });
    },
  });
};

// Company List Filter hooks

export const useGetCompanyListFilters = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  return useCustomQuery({
    queryKey: ["company-list-filters"],
    queryFn: () => get<GetUserFilterCompany>("/user/filter/company"),
    enabled: enabled ?? true,
  });
};

export const useCreateCompanyListFilter = () => {
  return useCustomMutation({
    mutationFn: (body: PostUserFilterCompany["body"]) =>
      post<PostUserFilterCompany>("/user/filter/company", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-list-filters"] });
    },
  });
};

export const useUpdateCompanyListFilter = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: PutUserFilterCompany["body"];
    }) => put<PutUserFilterCompany>(`/user/filter/company/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-list-filters"] });
    },
  });
};

export const useDeleteCompanyListFilter = () => {
  return useCustomMutation({
    mutationFn: ({ id }: { id: number }) =>
      del<DeleteUserFilterCompany>(`/user/filter/company/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-list-filters"] });
    },
  });
};

export const useGetExportCustomers = () => {
  return useCustomMutation({
    mutationFn: (body: PostExportCustomers["body"]) =>
      post<PostExportCustomers>(`/customer/export`, body),
  });
};

export const useGetCustomerNotes = (props: {
  params: GetCustomerNotes["params"];
  query: GetCustomerNotes["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetCustomerNotes", props],
    queryFn: () =>
      get<GetCustomerNotes>(
        `/customer/${props.params.customerId}/note`,
        props.query,
      ),
    enabled: !props.disabled,
  });
};

export const usePostCustomerNote = () => {
  return useCustomMutation({
    mutationFn: (props: {
      params: PostCustomerNote["params"];
      body: PostCustomerNote["body"];
    }) =>
      post<PostCustomerNote>(
        `/customer/${props.params.customerId}/note`,
        props.body,
      ),
    onSuccess: (_, props) => {
      queryClient.invalidateQueries({
        queryKey: [
          "useGetCustomerNotes",
          { params: { customerId: props.params.customerId } },
        ],
      });
    },
  });
};

export const usePutCustomerNote = () => {
  return useCustomMutation({
    mutationFn: (props: {
      params: PutCustomerNote["params"];
      body: PutCustomerNote["body"];
    }) =>
      put<PutCustomerNote>(
        `/customer/${props.params.customerId}/note/${props.params.noteId}`,
        props.body,
      ),
    onSuccess: (_, props) => {
      queryClient.invalidateQueries({
        queryKey: [
          "useGetCustomerNotes",
          { params: { customerId: props.params.customerId } },
        ],
      });
    },
  });
};

export const useDeleteCustomerNote = () => {
  return useCustomMutation({
    mutationFn: (props: {
      params: DeleteCustomerNote["params"];
      body: DeleteCustomerNote["body"];
    }) =>
      del<DeleteCustomerNote>(
        `/customer/${props.params.customerId}/note/${props.params.noteId}`,
        props.body,
      ),
    onSuccess: (_, props) => {
      queryClient.invalidateQueries({
        queryKey: [
          "useGetCustomerNotes",
          { params: { customerId: props.params.customerId } },
        ],
      });
    },
  });
};

export const useGetCustomerNote = (props: {
  params: GetCustomerNote["params"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetCustomerNote", props],
    queryFn: () =>
      get<GetCustomerNote>(
        `/customer/${props.params.customerId}/note/${props.params.noteId}`,
      ),
    enabled: !props.disabled,
  });
};

export const useGetCustomerNoteTag = (props: {
  params: GetCustomerNoteTag["params"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["useGetCustomerNoteTag", props],
    queryFn: () =>
      get<GetCustomerNoteTag>(`/customer-note/tag/${props.params.tagId}`),
    enabled: !props.disabled,
  });
};
